import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const RISK_PROFILES = [
    { id: "low", label: "Low" },
    { id: "medium", label: "Medium" },
    { id: "high", label: "High" },
];
const PROJECT_TYPES = [
    { id: "residential", label: "Residential" },
    { id: "commercial", label: "Commercial" },
    { id: "tourism", label: "Tourism / Resort" },
    { id: "mixed_use", label: "Mixed-use" },
    { id: "subdivision", label: "Subdivision" },
];

export default function ConvertToJVDialog({ listing, onClose, onSuccess }) {
    const [form, setForm] = useState({
        project_vision: "",
        project_type: "residential",
        capital_required_fjd: Math.round((listing.price_fjd || 500000) * 2),
        landowner_equity_pct: 25,
        timeline_months: 36,
        roi_estimate_pct: 19,
        risk_profile: "medium",
        financial_projections: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if ((form.project_vision || "").length < 20) {
            toast.error("Project vision must be at least 20 characters");
            return;
        }
        if ((form.financial_projections || "").length < 10) {
            toast.error("Financial projections must be at least 10 characters");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/listings/${listing.id}/convert-to-jv`, form);
            toast.success("Listing converted to a JV — pending vetting review.");
            onSuccess();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not convert");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="convert-to-jv-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">Convert to Joint Venture</DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        Submit on behalf of <strong className="text-foreground">{listing.owner_name}</strong> · Earns you the JV co-origination kickback when this closes.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3" data-testid="convert-to-jv-form">
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Project vision *</Label>
                        <Textarea
                            data-testid="convert-vision"
                            rows={3}
                            value={form.project_vision}
                            onChange={(e) => update("project_vision", e.target.value)}
                            placeholder="What will be built / operated on the site?"
                            className="bg-background text-foreground resize-none"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Project type *</Label>
                            <Select value={form.project_type} onValueChange={(v) => update("project_type", v)}>
                                <SelectTrigger data-testid="convert-type" className="bg-background text-foreground"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PROJECT_TYPES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Risk *</Label>
                            <Select value={form.risk_profile} onValueChange={(v) => update("risk_profile", v)}>
                                <SelectTrigger data-testid="convert-risk" className="bg-background text-foreground"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {RISK_PROFILES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Capital required (FJD) *</Label>
                            <Input
                                type="number"
                                data-testid="convert-capital"
                                value={form.capital_required_fjd}
                                onChange={(e) => update("capital_required_fjd", Number(e.target.value))}
                                className="bg-background text-foreground"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Landowner equity %</Label>
                            <Input
                                type="number"
                                data-testid="convert-equity"
                                value={form.landowner_equity_pct}
                                onChange={(e) => update("landowner_equity_pct", Number(e.target.value))}
                                min={5} max={50}
                                className="bg-background text-foreground"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Timeline (months)</Label>
                            <Input
                                type="number"
                                data-testid="convert-timeline"
                                value={form.timeline_months}
                                onChange={(e) => update("timeline_months", Number(e.target.value))}
                                className="bg-background text-foreground"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Expected ROI %</Label>
                            <Input
                                type="number"
                                data-testid="convert-roi"
                                value={form.roi_estimate_pct}
                                onChange={(e) => update("roi_estimate_pct", Number(e.target.value))}
                                className="bg-background text-foreground"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-app text-muted-foreground">Financial projections *</Label>
                        <Textarea
                            data-testid="convert-projections"
                            rows={3}
                            value={form.financial_projections}
                            onChange={(e) => update("financial_projections", e.target.value)}
                            placeholder="Revenue, key cost lines, expected returns…"
                            className="bg-background text-foreground resize-none"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={onClose} className="font-app">Cancel</Button>
                        <Button type="submit" disabled={submitting} data-testid="convert-submit-btn" className="bg-coral hover:bg-coral-deep text-white">
                            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {submitting ? "Submitting…" : "Submit JV"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
